// frontend/src/components/dashboard/tabs/DashboardAITutorsTab.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tabs,
    Tab,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import GradientButton from '../../../components/ui/GradientButton';
import {
    Search,
    Plus,
    Bot,
    Database,
    ChevronDown,
    Users,
} from 'lucide-react';
import { useTutorsStore } from '../../../stores';
import { subscriptionsApi } from '../../../services/api';
import TutorCard from './TutorCard';
import CreateTutorDialog from './CreateTutorDialog';
import CreateRepositoryDialog from './CreateRepositoryDialog';
import RepositoryCard from './RepositoryCard';

const DashboardAITutorsTab = ({ token, setError }) => {
    const { t } = useTranslation();

    // Use Zustand stores
    const {
        aiTutors,
        repositories,
        fetchAITutors,
        fetchRepositories
    } = useTutorsStore();

    // State для выпадающего меню "Создать"
    const [anchorEl, setAnchorEl] = useState(null);
    const [contentTab, setContentTab] = useState(0); // 0 - Тьюторы, 1 - Репозитории

    const [sharedTutors, setSharedTutors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingShared, setIsLoadingShared] = useState(true);
    const [isLoadingRepositories, setIsLoadingRepositories] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [, setIsLoadingSubscription] = useState(true);

    // State для диалогов
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createRepositoryDialogOpen, setCreateRepositoryDialogOpen] = useState(false);

    // Загрузка данных при монтировании
    useEffect(() => {
        loadSubscriptionStatus();

        if (contentTab === 0) {
            loadTutors();
            loadSharedTutors();
        } else {
            loadRepositories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, contentTab]);

    // Загрузка статуса подписки
    const loadSubscriptionStatus = async () => {
        setIsLoadingSubscription(true);
        try {
            const response = await subscriptionsApi.getStatus(token);
            setSubscriptionStatus(response);
        } catch (err) {
            setError(t('subscription.error.loading'));
        } finally {
            setIsLoadingSubscription(false);
        }
    };

    // Обработчики меню "Создать"
    const handleOpenCreateMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseCreateMenu = () => {
        setAnchorEl(null);
    };

    // Обработчики содержимого
    const handleCreateTutorClick = () => {
        // Проверяем, может ли пользователь создать еще одного тьютора
        if (subscriptionStatus?.can_create && !subscriptionStatus.can_create.tutor) {
            setError(t('subscription.error.tutor_limit_reached', {
                max: subscriptionStatus.limits?.tutors?.max || 0
            }));
            handleCloseCreateMenu();
            return;
        }

        setCreateDialogOpen(true);
        handleCloseCreateMenu();
    };

    const handleCreateRepositoryClick = () => {
        // Проверяем, может ли пользователь создать еще один репозиторий
        // Для репозиториев используем лимит шаблонов, так как отдельного лимита для репозиториев нет
        if (subscriptionStatus?.can_create && !subscriptionStatus.can_create.template) {
            setError(t('subscription.error.repository_limit_reached', {
                max: subscriptionStatus.limits?.templates?.max || 0
            }));
            handleCloseCreateMenu();
            return;
        }

        setCreateRepositoryDialogOpen(true);
        handleCloseCreateMenu();
    };

    // Обработчик изменения вкладки контента
    const handleContentTabChange = (event, newValue) => {
        setContentTab(newValue);
    };

    // Загрузка данных
    const loadTutors = async () => {
        setIsLoading(true);
        try {
            await fetchAITutors('owned', token);
        } catch (err) {
            setError(t('dashboard.ai_tutors.error.loading'));
        } finally {
            setIsLoading(false);
        }
    };

    const loadSharedTutors = async () => {
        setIsLoadingShared(true);
        try {
            const sharedData = await fetchAITutors('shared', token);
            setSharedTutors(sharedData.map(tutor => ({...tutor, isOwned: false})));
        } catch (err) {
            setSharedTutors([]);
        } finally {
            setIsLoadingShared(false);
        }
    };

    const loadRepositories = async () => {
        setIsLoadingRepositories(true);
        try {
            await fetchRepositories(token);
        } catch (err) {
            setError(t('dashboard.ai_tutors.error.loading_repositories'));
        } finally {
            setIsLoadingRepositories(false);
        }
    };

    // Обработчик создания тьютора
    const handleCreateTutor = async (_newTutor) => {
        try {
            await loadTutors();
        } catch (err) {
            setError(t('dashboard.ai_tutors.error.creating'));
        }
    };

    // Обработчик создания репозитория
    const handleRepositoryCreated = async (_newRepository) => {
        await loadRepositories();
    };

    // Обработчик удаления тьютора
    const handleDeleteTutor = async (_tutorId) => {
        await loadTutors();
    };

    // Получение отфильтрованных тьюторов и репозиториев
    const getFilteredTutors = () => {
        // Добавляем флаг isOwned для всех owned тьюторов
        let filtered = aiTutors.map(tutor => ({...tutor, isOwned: true}));

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(tutor =>
                tutor.name.toLowerCase().includes(term)
            );
        }

        // console.log('Filtered owned tutors:', filtered);
        return filtered;
    };

    const getFilteredSharedTutors = () => {
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return sharedTutors.filter(tutor =>
                tutor.name.toLowerCase().includes(term)
            );
        }

        return sharedTutors;
    };


    // Фильтрованные тьюторы для отображения
    const filteredTutors = getFilteredTutors();
    const filteredSharedTutors = getFilteredSharedTutors();

    return (
        <div className="space-y-6">
            {/* Панель поиска и создания */}
            <div className="flex flex-col md:flex-row gap-4 items-center mb-6">
                <TextField
                    placeholder={t('dashboard.ai_tutors.search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-grow"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search className="text-gray-400" />
                            </InputAdornment>
                        ),
                    }}
                    size="small"
                    fullWidth
                />

                <GradientButton
                    onClick={handleOpenCreateMenu}
                    endIcon={<ChevronDown className="w-4 h-4" />}
                    startIcon={<Plus className="w-4 h-4" />}
                >
                    {t('common.create')}
                </GradientButton>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseCreateMenu}
                >
                    <MenuItem onClick={handleCreateTutorClick}>
                        <ListItemIcon>
                            <Bot className="w-5 h-5 text-purple-500" />
                        </ListItemIcon>
                        <ListItemText>
                            {t('dashboard.ai_tutors.create_button')}
                        </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCreateRepositoryClick}>
                        <ListItemIcon>
                            <Database className="w-5 h-5 text-blue-500" />
                        </ListItemIcon>
                        <ListItemText>
                            {t('dashboard.ai_tutors.repository_dialog.create_button')}
                        </ListItemText>
                    </MenuItem>
                </Menu>
            </div>

            {/* Вкладки контента */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={contentTab}
                    onChange={handleContentTabChange}
                    variant="fullWidth"
                >
                    <Tab
                        icon={<Bot className="w-4 h-4 mr-2 text-purple-500" />}
                        label={t('dashboard.tabs.ai_tutors')}
                        iconPosition="start"
                    />
                    <Tab
                        icon={<Database className="w-4 h-4 mr-2 text-blue-500" />}
                        label={t('dashboard.ai_tutors.repository_dialog.repositories')}
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {/* Контент вкладки Тьюторы */}
            {contentTab === 0 && (
                <div className="space-y-6">
                    {/* Мои тьюторы */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <Bot className="w-5 h-5 text-purple-500" />
                                <Typography variant="h6">
                                    {t('dashboard.ai_tutors.my_tutors')}
                                </Typography>
                                <Chip
                                    label={filteredTutors.length}
                                    size="small"
                                    sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
                                />
                            </div>

                            {isLoading ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <CircularProgress />
                                    <Typography variant="body1" className="mt-4 text-gray-600">
                                        {t('dashboard.ai_tutors.loading')}
                                    </Typography>
                                </div>
                            ) : filteredTutors.length === 0 ? (
                                <Alert severity="info" className="my-6">
                                    {searchTerm
                                        ? t('dashboard.ai_tutors.no_results')
                                        : t('dashboard.ai_tutors.no_tutors')}
                                </Alert>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredTutors.map(tutor => (
                                        <TutorCard
                                            key={tutor.id}
                                            tutor={tutor}
                                            token={token}
                                            onDelete={handleDeleteTutor}
                                            isOwned={tutor.isOwned}
                                            setError={setError}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Доступные мне тьюторы */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <Users className="w-5 h-5 text-purple-500" />
                                <Typography variant="h6">
                                    {t('dashboard.ai_tutors.shared_tutors')}
                                </Typography>
                                <Chip
                                    label={filteredSharedTutors.length}
                                    size="small"
                                    sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                                />
                            </div>

                            {isLoadingShared ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <CircularProgress />
                                    <Typography variant="body1" className="mt-4 text-gray-600">
                                        {t('dashboard.ai_tutors.loading')}
                                    </Typography>
                                </div>
                            ) : filteredSharedTutors.length === 0 ? (
                                <Alert severity="info" className="my-6">
                                    {searchTerm
                                        ? t('dashboard.ai_tutors.no_shared_results')
                                        : t('dashboard.ai_tutors.no_shared_tutors')}
                                </Alert>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredSharedTutors.map(tutor => (
                                        <TutorCard
                                            key={tutor.id}
                                            tutor={tutor}
                                            token={token}
                                            isOwned={false}
                                            setError={setError}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Контент вкладки Репозитории */}
            {contentTab === 1 && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4">
                        <div className="flex items-center gap-2 mb-4">
                            <Database className="w-5 h-5 text-blue-500" />
                            <Typography variant="h6">
                                {t('dashboard.ai_tutors.repository_dialog.my_repositories')}
                            </Typography>
                        </div>

                        {isLoadingRepositories ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <CircularProgress />
                                <Typography variant="body1" className="mt-4 text-gray-600">
                                    {t('dashboard.loading')}
                                </Typography>
                            </div>
                        ) : repositories.length === 0 ? (
                            <Alert severity="info" className="my-6">
                                {t('dashboard.ai_tutors.repository_dialog.no_repositories')}
                            </Alert>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                                {repositories.map(repo => (
                                    <RepositoryCard
                                        key={repo.id}
                                        repository={repo}
                                        token={token}
                                        setError={setError}
                                        onUpdate={loadRepositories}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Диалоги */}
            <CreateTutorDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSubmit={handleCreateTutor}
                token={token}
                setError={setError}
            />

            <CreateRepositoryDialog
                open={createRepositoryDialogOpen}
                onClose={() => setCreateRepositoryDialogOpen(false)}
                onSuccess={handleRepositoryCreated}
                token={token}
                setError={setError}
            />
        </div>
    );
};

export default DashboardAITutorsTab;
